import React, { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const MessageList = ({ messages, onMarkAsRead }) => {
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const messageRefs = useRef({});

  useEffect(() => {
    // Auto-scroll do najnowszych wiadomości
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    // Oznacz wiadomości jako przeczytane gdy są widoczne
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const messageId = entry.target.dataset.messageId;
            const message = messages.find(m => m.id === parseInt(messageId));
            if (message && !message.read && message.recipientId === user?.id && onMarkAsRead) {
              onMarkAsRead(message.id);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    Object.values(messageRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => {
      Object.values(messageRefs.current).forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [messages, user, onMarkAsRead]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (messageDate.getTime() === today.getTime()) {
      // Dziś - tylko godzina
      return date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    } else {
      // Wcześniej - data + godzina
      return date.toLocaleString('pl-PL', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  if (!messages || messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <p className="text-gray-500 text-sm">Brak wiadomości. Rozpocznij rozmowę!</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      {messages.map((message) => {
        const isOwnMessage = message.senderId === user?.id;
        const messageRef = (el) => {
          messageRefs.current[message.id] = el;
        };

        return (
          <div
            key={message.id}
            ref={messageRef}
            data-message-id={message.id}
            className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[75%] rounded-lg px-4 py-2 ${
                isOwnMessage
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}
            >
              {!isOwnMessage && (
                <p className="text-xs font-semibold mb-1 opacity-75">
                  {message.senderName || message.senderEmail}
                </p>
              )}
              <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
              <p className={`text-xs mt-1 ${isOwnMessage ? 'text-primary-100' : 'text-gray-500'}`}>
                {formatDate(message.createdAt)}
                {!message.read && isOwnMessage && (
                  <span className="ml-1">•</span>
                )}
              </p>
            </div>
          </div>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
